import { Box, Icon } from '@chakra-ui/react';
import type {
  RegistrationProgressResponse,
  RegistrationSuccessInfo,
  StatusVerificationSection,
} from '@diamond/shared/features/registration/browser';
import {
  ArrowCircleRightOutlined,
  ShoppingBagOutlined,
} from '@mui/icons-material';
import { type ReactElement, useEffect, useState } from 'react';

import {
  type RegistrationBusinessProfile,
  type RegistrationOutlet,
  DESKTOP_ATTACHMENT_META,
  displayValue,
  getOutletAttachmentLabel,
  resolveVerificationOutlet,
} from './NooRegistrationStatusConstants';
import {
  BusinessVerificationDetailBody,
  DetailAttachmentCard,
  DetailField,
} from './NooRegistrationStatusDetailFields';
import { VerificationSectionCard } from './NooRegistrationStatusSectionCard';
import {
  PendingReviewBanner,
  RegistrationSuccessBanner,
  SupportSection,
} from './NooRegistrationStatusSharedUi';

function DesktopOutletAttachments({
  outlet,
}: {
  outlet: RegistrationOutlet | undefined;
}) {
  return (
    <Box mt={8}>
      <Box
        display="grid"
        gridTemplateColumns="repeat(2, minmax(0, 1fr))"
        gap={6}
      >
        {DESKTOP_ATTACHMENT_META.map((item) => (
          <DetailAttachmentCard
            key={item.type}
            label={getOutletAttachmentLabel(item, outlet)}
            image={item.image}
            type={item.type}
            attachment={outlet?.attachments.find(
              (attachment) => attachment.attachment_type === item.type
            )}
          />
        ))}
      </Box>
    </Box>
  );
}

function DesktopBusinessDetails({
  business,
  outlets,
  selectedSection,
}: {
  business: RegistrationBusinessProfile | null;
  outlets: RegistrationOutlet[];
  selectedSection: StatusVerificationSection | undefined;
}): ReactElement | null {
  const selectedOutlet =
    selectedSection?.kind === 'outlet'
      ? resolveVerificationOutlet(outlets, selectedSection)
      : undefined;
  const isBusinessSelected = selectedSection?.kind === 'business';
  const isOutletSelected = selectedSection?.kind === 'outlet';

  if (isBusinessSelected && business) {
    return (
      <Box minW={0} data-selected="true">
        <BusinessVerificationDetailBody business={business} />
      </Box>
    ) as ReactElement;
  }

  if (isOutletSelected) {
    return (
      <Box minW={0}>
        <Box data-selected="true">
          <Box display="flex" alignItems="center" gap={3} flexWrap="wrap">
            <Box
              as="h2"
              fontSize="24px"
              lineHeight="29px"
              fontWeight="medium"
              color="#373F41"
            >
              {displayValue(selectedOutlet?.name)}
            </Box>
            {selectedOutlet?.is_primary ? (
              <Box
                as="span"
                px={3}
                py={1}
                borderRadius="full"
                bg="#F0FFF4"
                color="#34C759"
                fontSize="12px"
                lineHeight="15px"
                fontWeight="medium"
              >
                Outlet Utama
              </Box>
            ) : null}
          </Box>
          <Box mt={4} display="flex" flexDirection="column" gap={4}>
            <DetailField
              label="Kategori Bisnis"
              value={selectedOutlet?.cg_3_business_type}
            />
            <DetailField
              label="Social Media Usaha"
              value={selectedOutlet?.social_media}
            />
            <DetailField
              label="Link Merchant"
              value={selectedOutlet?.online_order_merchant_link}
            />
            <DetailField
              label="Nomor handphone PIC Outlet"
              value={selectedOutlet?.phone}
            />
            <DetailField label="Email" value={selectedOutlet?.email} />
          </Box>
        </Box>

        <DesktopOutletAttachments outlet={selectedOutlet} />
      </Box>
    ) as ReactElement;
  }

  return null;
}

function DesktopStatusTitleBar(): ReactElement {
  return (
    <Box
      display="flex"
      alignItems="center"
      justifyContent="space-between"
      gap={4}
      mb={8}
    >
      <Box
        as="h1"
        fontSize="28px"
        lineHeight="32px"
        fontWeight="normal"
        color="#373F41"
      >
        Status Verifikasi
      </Box>
      <Box
        as="button"
        type="button"
        disabled
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        h="48px"
        w="180px"
        px={6}
        borderRadius="8px"
        bg="#F2F2F7"
        color="#AEAEB2"
        fontSize="sm"
        fontWeight="medium"
        cursor="not-allowed"
        data-testid="noo-buyer-status-new-badge-desktop"
      >
        <Icon as={ShoppingBagOutlined} boxSize={5} />
        <span>Pengajuan Baru</span>
        <Icon as={ArrowCircleRightOutlined} boxSize={5} />
      </Box>
    </Box>
  ) as ReactElement;
}

function DesktopStatusLeftPanel({
  isStatusVerificationView,
  displayInfo,
  submittedAtLabel,
  showSuccessBanner,
  successInfo,
  desktopSections,
  registrationToken,
  selectedSection,
  onSelectSection,
}: {
  isStatusVerificationView: boolean;
  displayInfo: {
    companyOrOwnerName: string | null;
    picName: string | null;
  } | null;
  submittedAtLabel: string | null;
  showSuccessBanner: boolean;
  successInfo: RegistrationSuccessInfo | null;
  desktopSections: StatusVerificationSection[];
  registrationToken: string;
  selectedSection: StatusVerificationSection | undefined;
  onSelectSection: (sectionId: string) => void;
}): ReactElement {
  return (
    <Box minW={0} display="flex" flexDirection="column" gap={5}>
      {isStatusVerificationView ? (
        <Box>
          <Box
            fontSize="24px"
            lineHeight="36px"
            fontWeight="medium"
            color="#0941A8"
          >
            {displayValue(displayInfo?.companyOrOwnerName)}
          </Box>
          <Box
            fontSize="16px"
            lineHeight="36px"
            fontWeight="medium"
            color="#373F41"
          >
            PIC: {displayValue(displayInfo?.picName)}
          </Box>
          {submittedAtLabel ? (
            <Box fontSize="12px" lineHeight="15px" color="#AEAEB2">
              {submittedAtLabel}
            </Box>
          ) : null}
        </Box>
      ) : (
        <Box>
          <Box
            fontSize="24px"
            lineHeight="32px"
            fontWeight="medium"
            color="#00003D"
          >
            Progres Registrasi
          </Box>
          <Box mt={2} fontSize="sm" lineHeight="20px" color="#666666">
            Lengkapi setiap bagian di bawah untuk melanjutkan pengajuan rekanan.
          </Box>
        </Box>
      )}

      {isStatusVerificationView ? (
        showSuccessBanner && successInfo ? (
          <RegistrationSuccessBanner successInfo={successInfo} />
        ) : (
          <PendingReviewBanner />
        )
      ) : null}

      {desktopSections.length > 0 ? (
        <Box display="flex" flexDirection="column" gap={3}>
          {desktopSections.map((section) => (
            <VerificationSectionCard
              key={section.id}
              section={section}
              registrationToken={registrationToken}
              compact
              desktop
              selected={selectedSection?.id === section.id}
              onSelect={() => onSelectSection(section.id)}
              testIdSuffix="-desktop"
            />
          ))}
        </Box>
      ) : (
        <Box fontSize="sm" color="#637381">
          Belum ada data registrasi yang tersimpan.
        </Box>
      )}

      <SupportSection testId="noo-buyer-status-support-desktop" />
    </Box>
  ) as ReactElement;
}

export function DesktopStatusContent({
  progress,
  displayInfo,
  verificationSections,
  registrationToken,
  submittedAtLabel,
  successInfo,
  showSuccessBanner,
  isStatusVerificationView,
  initialSectionId,
}: {
  progress: RegistrationProgressResponse;
  displayInfo: {
    companyOrOwnerName: string | null;
    picName: string | null;
  } | null;
  verificationSections: StatusVerificationSection[];
  registrationToken: string;
  submittedAtLabel: string | null;
  successInfo: RegistrationSuccessInfo | null;
  showSuccessBanner: boolean;
  isStatusVerificationView: boolean;
  initialSectionId?: string | null;
}): ReactElement {
  const desktopSections = verificationSections.filter(
    (section) => section.id !== 'documents'
  );
  const [selectedSectionId, setSelectedSectionId] = useState<string | null>(
    null
  );

  useEffect(() => {
    if (!initialSectionId) {
      return;
    }

    const isKnownSection = desktopSections.some(
      (section) => section.id === initialSectionId
    );
    if (isKnownSection) {
      setSelectedSectionId(initialSectionId);
    }
  }, [desktopSections, initialSectionId]);

  const selectedSection =
    desktopSections.find((section) => section.id === selectedSectionId) ??
    desktopSections.find((section) => section.id === 'business') ??
    desktopSections[0];
  return (
    <Box bg="#FFFFFF" borderRadius="20px" p={8}>
      <DesktopStatusTitleBar />

      <Box
        display="grid"
        gridTemplateColumns="minmax(0, 592fr) minmax(0, 608fr)"
        gap={8}
        alignItems="start"
      >
        <DesktopStatusLeftPanel
          isStatusVerificationView={isStatusVerificationView}
          displayInfo={displayInfo}
          submittedAtLabel={submittedAtLabel}
          showSuccessBanner={showSuccessBanner}
          successInfo={successInfo}
          desktopSections={desktopSections}
          registrationToken={registrationToken}
          selectedSection={selectedSection}
          onSelectSection={setSelectedSectionId}
        />

        <DesktopBusinessDetails
          business={progress.business}
          outlets={progress.outlets}
          selectedSection={selectedSection}
        />
      </Box>
    </Box>
  ) as ReactElement;
}
